import React, { useMemo, useState } from "react";
import { Row, Col, Table, Button } from "reactstrap";
import i18n from "../../../i18n";
import { statusMap } from "../../../constants/utils";
import { BookingType, ServiceStatus, ServiceEventType } from "../../../constants/enums";
import GpxViewer from "../../../components/Common/GpxViewer";
import MapStatusMessage from "../../../components/Common/MapStatusMessage";

export const GeneralInfo = React.memo(({ service }) => (
  <>
    <h6 className="detail-subtitle">{i18n.t("Company")}:</h6>
    <p>{service.partner_name}</p>
    <h6 className="detail-subtitle">{i18n.t("Status")}:</h6>
    <p>{i18n.t(statusMap[service.status] || `${service.status}`)}</p>
    {[ServiceStatus.DELETED].includes(service.status) && (
      <>
        <h6 className="detail-subtitle">{i18n.t("CancellationReason")}:</h6>
        <p>{service.cancellation_info?.reason || i18n.t("NoCancellationReason")}</p>
      </>
    )}
    {[ServiceStatus.FINISHED, ServiceStatus.ASSIGNED, ServiceStatus.REJECTED, ServiceStatus.STARTED].includes(service.status) && (
      <>
        <h6 className="detail-subtitle">{i18n.t("DriverData")}:</h6>
        <p className="detail-text">{service.driver?.driver_name}</p>
        <p className="detail-text">{service.vehicle?.plate}</p>
        <p className="detail-text">{`${service.vehicle?.brand} ${service.vehicle?.sub_brand} ${service.vehicle?.model} ${service.vehicle?.color}`}</p>
      </>
    )}
  </>
));

export const ServiceDetails = React.memo(({ service }) => {
  const [selectedPod, setSelectedPod] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleViewPod = (pod) => {
    setSelectedPod(pod);
    setImageError(false);
  };

  const openImageModal = () => {
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
  };

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <Row>
      <Col xxl={6} md={6}>
        <h6 className="detail-subtitle">{i18n.t("DateAndHourService")}</h6>
        <p>{(service.trip.scheduled_start_at)}</p>
        {[ServiceStatus.FINISHED, ServiceStatus.ASSIGNED, ServiceStatus.REJECTED].includes(service.status) && (
          <>
            <h6 className="detail-subtitle">{i18n.t("DateAndHourServiceStart")}</h6>
            <p>
              {service?.trip?.started_at
                ? service.trip.started_at
                : i18n.t("ServiceNotStartedMessage")}
            </p>
            <h6 className="detail-subtitle">{i18n.t("DateAndHourServiceEnd")}</h6>
            <p>
              {service?.trip?.ended_at
                ? service.trip.ended_at
                : i18n.t("ServiceNotEndedMessage")}
            </p>
            <h6 className="detail-subtitle">{i18n.t("TypeService")}:</h6>
            <p>{i18n.t(service.type)}</p>
            <h6 className="detail-subtitle">{i18n.t("UnitsRequested")}</h6>
            <p>{service.info?.num_vehicles ?? 1}</p>
          </>
        )}
        {[ServiceStatus.FINISHED].includes(service.status) && (
          <>
            <h6 className="detail-subtitle">{i18n.t("ServiceTime")}</h6>
            <p>{(service.trip.time)}</p>
            <h6 className="detail-subtitle">{i18n.t("ServiceDistance")}</h6>
            <p>{(service.trip.distance)}</p> 
          </>
        )}   
        <h6 className="detail-subtitle">{i18n.t("AdditionalNote")}:</h6>
        <p>
          {service.info?.notes?.[0]?.message 
            ? service.info.notes[0].message 
            : i18n.t("NoAdditionalNoteMessage")
          }
        </p>
        {service.pods && service.pods.length > 0 && (
          <>
            <h6 className="detail-subtitle mt-4">{i18n.t("ProofsOfService")}</h6>
            <div className="table-responsive">
              <Table bordered className="no-vertical-borders">
                <thead>
                  <tr>
                    <th className="bg-light">{i18n.t("Name")}</th>
                    <th className="bg-light">{i18n.t("Action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {service.pods.map((pod, index) => (
                    <tr key={index}>
                      <td>{i18n.t(pod.type)}</td>
                      <td>
                        <Button 
                          className="bg-users border custom-pod-button"
                          onClick={() => handleViewPod(pod)}
                        >
                          {i18n.t("View")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Col>
      <Col xxl={6} md={6}>
        <div className="pod-preview-content">
          {selectedPod ? (
            <>
              <div className="pod-title-container">
                <h6 className="detail-subtitle">{i18n.t("ProofsOfService")}</h6>
                <div className="pod-meta">
                  <span className="pod-type">
                    {i18n.t(selectedPod.type)}
                  </span>
                </div>
              </div>
              {imageError ? (
                <div className="image-error-message">
                  <i className="bx bx-error-circle bx-lg text-danger mb-2"></i>
                  <p>{i18n.t("ImageNotAvailable")}</p>
                </div>
              ) : (
                <div className="pod-image-container" onClick={openImageModal}>
                  <img 
                    src={selectedPod.url} 
                    alt={`POD ${selectedPod.type}`} 
                    className="img-fluid pod-image"
                    onError={handleImageError}
                  />
                  <div className="image-overlay">
                    <i className="bx bx-zoom-in"></i> {i18n.t("ClickToZoom")}
                  </div>
                </div>
              )}
            </>
          ) : (
            service.pods && service.pods.length > 0 && (
              <div className="no-pod-selected">
                <i className="bx bx-image-alt bx-lg"></i>
                <p>{i18n.t("SelectPodToPreview")}</p>
              </div>
            )
          )}
        </div>
      </Col>
      {showImageModal && selectedPod && !imageError && (
        <div className="modal-overlay" onClick={closeImageModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h5>{i18n.t(selectedPod.type)} - {i18n.t(selectedPod.status)}</h5>
              <button type="button" className="close" onClick={closeImageModal}>
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <img 
                src={selectedPod.url} 
                alt={`POD ${selectedPod.type}`} 
                className="img-fluid"
              />
            </div>
            <div className="orange-separator"/>
          </div>
        </div>
      )}
    </Row>
  );
});

export const Passengers = React.memo(({ service }) => {
  const riders = service?.info?.riders || [];

  return (
    <>
      {riders.length > 0 ? (
        riders.slice(0, 4).map((rider, index) => (
          <div key={rider.id || index}>
            <h6 className="detail-subtitle">{`${i18n.t("Passenger")} ${index + 1}`}</h6>
            <p className="detail-text">{rider.name}</p>
            <p className="detail-text">{rider?.phone}</p>
            {rider?.available_trips > 0 && (
              <p className="detail-text">
                {rider.available_trips} {i18n.t("ServicesAvailable")}
              </p>
            )}
            <p/>
          </div>
        ))
      ) : (
        <>
          <h6 className="detail-subtitle">{i18n.t("Passenger")}</h6>
          <p>{i18n.t("NoRidersRegistered")}</p>
        </>
      )}
    </>
  );
});

export const Addresses = React.memo(({ service, origin, destination, scales, gpxData }) => (
  <Row>
    <Col xxl={6} md={6}>
      <h6 className="detail-subtitle">{i18n.t("Origin")}</h6>
      {origin && <p className="detail-text">{`${origin.address.description}`}</p>}
      <p>{(service.trip.started_at)}</p>
      <h6 className="detail-subtitle">{i18n.t("Destiny")}</h6>
      <p className="detail-text">
        {destination?.address?.description || i18n.t("NoDestiny")}
      </p>
      {[ServiceStatus.FINISHED, ServiceStatus.ASSIGNED, ServiceStatus.REJECTED].includes(service.status) && (
        <p>{(service.trip.ended_at)}</p>
      )}
      <p></p>
      <h6 className="detail-subtitle">{i18n.t("Scales")}</h6>
      {scales && scales.length > 0 ? (
        <div>
          {scales.map((scale, index) => (
            <p key={index}>
              {`${scale.address.description} (${i18n.t("Scale")} ${index + 1})`}
            </p>
          ))}
        </div>
      ) : (
        <p>{i18n.t("NoScalesRegistered")}</p>
      )}
    </Col>
    <Col xxl={6} md={6} xl={6} xs={12} className="gpx-map-container">
      <h6 className="detail-subtitle">{i18n.t("ServiceMap")}</h6>
      {gpxData ? (
        <div style={{ height: '400px', position: 'relative', width: '100%' }}>
          <GpxViewer gpxData={gpxData} />
        </div>
      ) : service.status === BookingType.FINISHED && service.trip?.url_image ? (
        <div style={{ height: '280px', overflow: 'hidden' }}>
          <img 
            src={service.trip.url_image} 
            alt="ServiceMap" 
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
          />
        </div>
      ) : (
        <div style={{ 
          height: '400px', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          backgroundColor: '#F8F9FA',
          borderRadius: '8px'
        }}>
          <MapStatusMessage status={service.status} />
        </div>
      )}
    </Col>
  </Row>
));

export const Actions = React.memo(({ service, timelineData, getEventDetails, formatDateTime }) => {
  const createdRequestEvent = (timelineData || []).find(
    event => event && event.action === "request" && event.requested_by?.name
  );

  const serviceEvents = useMemo(() => {
    const events = [
      {
        type: ServiceEventType.CREATED,
        date: service.trip?.scheduled_start_at ||
              (service.scheduled_start ? `${service.scheduled_start.date} ${service.scheduled_start.time}` : null),
        create_date: createdRequestEvent?.create_date,
        create_time: createdRequestEvent?.create_time,
        status: "COMPLETED",
        requested_by: createdRequestEvent?.requested_by,
        action_by: createdRequestEvent?.action_by
      },
      ...(timelineData || []).filter(event => event && event.action === "request"),
      ...(timelineData || []).filter(event => event && event.action === "asigment").map(event => ({
        ...event,
        formattedDate: formatDateTime(event.date, event.time)
      })),
      {
        type: ServiceEventType.STARTED,
        date: service.trip?.started_at,
        status: "COMPLETED"
      },
      ...(timelineData || []).filter(event => event && event.action === "stop" && event.type === ServiceEventType.ORIGIN_PICKUP),
      ...(timelineData || []).filter(event => event && event.action === "stop" && event.type === ServiceEventType.DESTINATION_DROPOFF),
      {
        type: ServiceEventType.COMPLETED,
        date: service.trip?.ended_at,
        status: "COMPLETED"
      },
      ...(service.status === ServiceStatus.DELETED ? [{
        type: ServiceEventType.CANCELLED,
        date: service.trip?.ended_at || service.trip?.started_at || service.trip?.scheduled_start_at,
        status: "CANCELLED",
        cancellation_info: service.cancellation_info
      }] : [])
    ].filter(event => event && (event.date || event.formattedDate || (event.create_date && event.create_time)));

    return events.map(event => {
      const details = getEventDetails(event);
      return details ? { ...event, details } : null;
    }).filter(Boolean);
  }, [service, timelineData, formatDateTime, getEventDetails]);

  return (
    <div className="timeline-container">
      {serviceEvents.length > 0 ? (
        <div className="vertical-timeline">
          {serviceEvents.map((event, index) => {
            const requestedBy = event.requested_by?.name;
            const actionBy = event.action_by?.name;

            return (
              <div key={index} className={`timeline-item ${event.details.status}`}>
                <div className="timeline-date">
                  {event.formattedDate 
                    ? event.formattedDate
                    : event.type === ServiceEventType.CREATED && event.create_date && event.create_time
                      ? formatDateTime(`${event.create_date} ${event.create_time?.split(".")[0]}`)
                      : formatDateTime(event.date)}
                </div>
                <div className="timeline-icon">
                  <i className={event.details.icon}></i>
                </div>
                <div className="timeline-content">
                  <h4>{event.details.title}</h4>
                  <p>
                    {event.details.description}
                    {event.type === ServiceEventType.CREATED && requestedBy && (
                      <>
                        <br />
                        <strong>{i18n.t("RequestedBy")}:</strong> {requestedBy}
                        <br />
                        <strong>{i18n.t("User")}:</strong> {actionBy}
                      </>
                    )}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p>{i18n.t("NoTimelineDataAvailable")}</p>
      )}
    </div>
  );
});
